import { useEffect, useMemo } from 'react'
import { useMap } from 'react-leaflet'
import { composePlacesBounds } from '../../../local/utils'
import PlaceMarker from './PlaceMarker'
import { toString, keyBy } from 'lodash'

/**
 * @typedef {import('../../../definitions').LeafletPlace} LeafletPlace
 * @typedef {import('leaflet').LatLngTuple} LatLngTuple
 */

/**
 * @typedef {Object} LeafletMapPlacesProps
 * @property {LeafletPlace[]} places An array of Leaflet places to draw in map.
 * @property {string=} selectedPlaceId ID of a place that should be highlighted.
 */

/**
 * Renders LeafletMap places markers with information popup.
 * @param {LeafletMapPlacesProps} props
 * @return {JSX.Element}
 */
const LeafletMapPlaces = ({ places, selectedPlaceId }) => {
  const map = useMap()

  const placesIndex = useMemo(() => {
    return keyBy(places, 'id')
  }, [places])

  useEffect(() => {
    const selectedPlace = placesIndex[selectedPlaceId] || null
    if (selectedPlace) {
      /**
       * @type {LatLngTuple}
       */
      const selectedLatLon = [selectedPlace.latitude, selectedPlace.longitude]
      // When a place is selected fly to a marker focus directly using a zoom
      // level where markers clustering is disabled so is possible to see
      // the exact location.
      map.flyTo(selectedLatLon, 17, { animate: false })
    } else {
      // Focus to place bounds when no specific place is selected to have
      // more panoramic view with markers clustering.
      const placeBounds = composePlacesBounds(places)
      map.invalidateSize()
      map.fitBounds(placeBounds, { padding: [20, 20] })
    }
  }, [places, selectedPlaceId])

  // For search result detail map there is a problem on render in Drupal
  // that needs re-rendering once the places component mounts.
  useEffect(() => {
    if (places.length === 1) {
      map.invalidateSize()
    }
  }, [])

  const PlaceMarkers = places.map((place) => {
    const isSelected = selectedPlaceId === toString(place.id)
    return <PlaceMarker key={place.id} place={place} isSelected={isSelected} />
  })

  return <>{PlaceMarkers}</>
}

export default LeafletMapPlaces
